import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronRight, ChevronDown, Sparkles, Plus, Minus, ExternalLink } from 'lucide-react';
import { StreamingText } from './AITypingEffect';
import type { MindmapNode as MindmapNodeType } from './mockData';
import { getNodeColors } from './mockData';

interface MindmapNodeProps {
  node: MindmapNodeType;
  level: number;
  isExpanded: boolean;
  onToggle: () => void;
  onDetailClick?: () => void;
  delay?: number;
}

/**
 * Interactive mindmap node with expand/collapse and AI typing reveal.
 */
export function MindmapNode({
  node,
  level,
  isExpanded,
  onToggle,
  onDetailClick,
  delay = 0,
}: MindmapNodeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [hasRevealed, setHasRevealed] = useState(false);
  const colors = getNodeColors(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const hasDetail = node.detailContent;

  // Node sizing based on level
  const sizes = {
    0: { width: 'min-w-[280px] max-w-[400px]', padding: 'p-6', title: 'text-xl', subtitle: 'text-sm' },
    1: { width: 'min-w-[240px] max-w-[320px]', padding: 'p-5', title: 'text-lg', subtitle: 'text-xs' },
    2: { width: 'min-w-[200px] max-w-[300px]', padding: 'p-4', title: 'text-base', subtitle: 'text-xs' },
  };
  const size = sizes[Math.min(level, 2) as keyof typeof sizes];

  // Sentiment indicator
  const sentimentColors = {
    positive: 'bg-emerald-500',
    negative: 'bg-rose-500',
    neutral: 'bg-slate-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, x: level === 0 ? 0 : -20 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      transition={{
        delay,
        type: 'spring',
        stiffness: 300,
        damping: 25,
      }}
      className="relative"
    >
      {/* Main node container */}
      <motion.div
        className={`
          ${size.width} ${size.padding}
          bg-[#063F6B]/80 backdrop-blur-sm
          rounded-2xl border-2 ${colors.border}
          cursor-pointer select-none
          transition-all duration-300
          ${isHovered ? `shadow-lg ${colors.glow} shadow-xl` : 'shadow-md'}
          ${isExpanded ? 'ring-2 ring-amber-500/30' : ''}
        `}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onToggle}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Header row */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Sentiment indicator */}
            {node.sentiment && (
              <div className={`w-2 h-2 rounded-full ${sentimentColors[node.sentiment]} flex-shrink-0`} />
            )}
            
            {/* Title */}
            <div className="flex-1 min-w-0">
              <h3 className={`font-bold text-white ${size.title} truncate`}>
                {node.title}
              </h3>
              {node.subtitle && (
                <p className={`text-slate-400 ${size.subtitle} truncate`}>
                  {node.subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Expand/collapse indicator */}
          {hasChildren && (
            <motion.div
              animate={{ rotate: isExpanded ? 90 : 0 }}
              transition={{ duration: 0.2 }}
              className="flex-shrink-0"
            >
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </motion.div>
          )}
        </div>

        {/* Hover preview / Summary */}
        <AnimatePresence>
          {isHovered && !isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-3 pt-3 border-t border-slate-700/50"
            >
              <p className="text-sm text-slate-300 line-clamp-2">
                {node.summary}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Expanded content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 pt-4 border-t border-slate-700/50"
              onAnimationComplete={() => setHasRevealed(true)}
            >
              {/* AI-typed content */}
              <div className="text-sm text-slate-200 leading-relaxed mb-4">
                <StreamingText
                  text={node.content}
                  isActive={isExpanded && !hasRevealed}
                  speed={15}
                />
              </div>

              {/* AI indicator */}
              <div className="flex items-center gap-2 text-xs text-amber-400/70 mb-3">
                <Sparkles className="w-3 h-3" />
                <span>AI-generated insight</span>
              </div>

              {/* Detail button */}
              {hasDetail && (
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDetailClick?.();
                  }}
                  className={`
                    w-full mt-2 px-4 py-2
                    ${colors.bg} border ${colors.border}
                    rounded-lg text-white/80 text-sm font-medium
                    flex items-center justify-center gap-2
                    hover:text-white hover:bg-white/[0.08] transition-all
                  `}
                >
                  <ExternalLink className="w-4 h-4" />
                  Deep Dive
                </motion.button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Connection point indicator */}
      {hasChildren && (
        <motion.div
          className={`
            absolute top-1/2 -right-2 w-4 h-4
            rounded-full bg-gradient-to-r ${colors.gradient}
            flex items-center justify-center
            transform -translate-y-1/2
          `}
          animate={{
            scale: isExpanded ? [1, 1.2, 1] : 1,
          }}
          transition={{ duration: 0.5 }}
        >
          {isExpanded ? (
            <Minus className="w-2.5 h-2.5 text-white" />
          ) : (
            <Plus className="w-2.5 h-2.5 text-white" />
          )}
        </motion.div>
      )}
    </motion.div>
  );
}

// Compact node variant for the tree structure
interface CompactNodeProps {
  node: MindmapNodeType;
  isSelected: boolean;
  onClick: () => void;
  delay?: number;
}

export function CompactNode({ node, isSelected, onClick, delay = 0 }: CompactNodeProps) {
  const colors = getNodeColors(node.id);
  const sentimentColors = {
    positive: 'bg-emerald-500',
    negative: 'bg-rose-500',
    neutral: 'bg-slate-400',
  };

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, type: 'spring', stiffness: 300, damping: 25 }}
      onClick={onClick}
      className={`
        px-4 py-2.5 rounded-xl
        bg-[#063F6B]/80 backdrop-blur-sm
        border-2 ${isSelected ? 'border-amber-500' : colors.border}
        text-left min-w-[160px]
        hover:shadow-lg transition-all duration-200
        ${isSelected ? 'ring-2 ring-amber-500/30' : ''}
      `}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
    >
      <div className="flex items-center gap-2">
        {node.sentiment && (
          <div className={`w-1.5 h-1.5 rounded-full ${sentimentColors[node.sentiment]}`} />
        )}
        <span className="font-semibold text-white text-sm">{node.title}</span>
      </div>
      {node.subtitle && (
        <p className="text-xs text-slate-400 mt-0.5 truncate">{node.subtitle}</p>
      )}
    </motion.button>
  );
}
